<template>
    <div>
        <div class="text-left m-8 text-sm">
            <p class="mb-4">Your personal statement is an important part of your application.  It is your opportunity to create a unique picture of who you are and why you'll be successful in your chosen course of study.  Please demonstrate your passion for the subject area and tell us about your motivation and commitment to your studies. Also, please tell us why the programme is suitable for you and how it fits into your career plans.</p>
            <p class="mb-4">Before writing your personal statement, we encourage you to carefully read the entry requirements for your chosen programme(s).  Where the programme requires numeracy skills and/or desirable professional experience, please be sure to state how you meet this requirement in your statement and in the relevant sections of the application form. </p>
            <p class="mb-4">Finally, your personal statement should be personal to you and written by you. It is an opportunity to showcase your skills, ambitions, and experiences in your own voice, which is what our academic colleagues are most interested in. The use of any AI tools are not permitted.</p>
            <p>Please note, you can only submit one personal statement.  If you are applying for two programmes, please ensure that the statement covers both choices.</p>
        </div>
        <h1 class="mx-8 text-green-600 text-lg text-left">Please type your personal statement here:</h1>
        <div class="mx-8 border border-gray-300 bg-gray-100 mb-8 p-4">
            <form @submit.prevent="update" @change="dirtyForm()" @keydown="dirtyForm">
                <div v-if="applicationData.supportMultiPersonalStatement">
                    <div class="flex flex-col-2">
                        <div class="w-1/2  text-left">
                            <p class="pl-1">Passion for the subject:</p>
                        </div>
                        <div class="w-1/2 text-right pr-2">
                            <p>Word count: <span :class="(passionWordCount > maximumWordCount)? 'bg-red-300' : ''">{{ passionWordCount }}</span> / {{ maximumWordCount }}</p>
                        </div>
                    </div>
                    <textarea-input
                        v-model="personalStatement.passion"
                        class="w-full-i bg-green-200 padding-2 mb-8"
                        label=""
                        @update:modelValue="value = $event"
                    />
                    <div v-if="passionWordCount > maximumWordCount" class="flex justify-end mt-2 -mb-4">
                        <p class="text-right bg-red-300 px-2">Your 'Passion for the subject' is too long. The maximum word count is {{ maximumWordCount }} words</p>
                    </div>

                    <div class="flex flex-col-2">
                        <div class="w-1/2  text-left">
                            <p class="pl-1">Motivation and commitment to their studies:</p>
                        </div>
                        <div class="w-1/2 text-right pr-2">
                            <p>Word count: <span :class="(motivationWordCount > maximumWordCount)? 'bg-red-300' : ''">{{ motivationWordCount }}</span> / {{ maximumWordCount }}</p>
                        </div>
                    </div>
                    <textarea-input
                        v-model="personalStatement.motivation"
                        class="w-full-i bg-green-200 padding-2 mb-8"
                        label=""
                        @update:modelValue="value = $event"
                    />
                    <div v-if="motivationWordCount > maximumWordCount" class="flex justify-end mt-2 -mb-4">
                        <p class="text-right bg-red-300 px-2">Your 'Motivation and commitment to their studies' is too long. The maximum word count is {{ maximumWordCount }} words</p>
                    </div>

                    <div class="flex flex-col-2">
                        <div class="w-1/2  text-left">
                            <p class="pl-1">Future aspirations in the chosen field:</p>
                        </div>
                        <div class="w-1/2 text-right pr-2">
                            <p>Word count: <span :class="(aspirationWordCount > maximumWordCount)? 'bg-red-300' : ''">{{ aspirationWordCount }}</span> / {{ maximumWordCount }}</p>
                        </div>
                    </div>
                    <textarea-input
                        v-model="personalStatement.aspiration"
                        class="w-full-i bg-green-200 padding-2 mb-8"
                        label=""
                        @update:modelValue="value = $event"
                    />
                    <div v-if="aspirationWordCount > maximumWordCount" class="flex justify-end mt-2 -mb-4">
                        <p class="text-right bg-red-300 px-2">Your 'Future aspirations in the chosen field' is too long. The maximum word count is {{ maximumWordCount }} words</p>
                    </div>

                    <div class="flex flex-col-2">
                        <div class="w-1/2  text-left">
                            <p class="pl-1">Other relevant achievements:</p>
                        </div>
                        <div class="w-1/2 text-right pr-2">
                            <p>Word count: <span :class="(otherWordCount > maximumWordCount)? 'bg-red-300' : ''">{{ otherWordCount }}</span> / {{ maximumWordCount }}</p>
                        </div>
                    </div>
                    <textarea-input
                        v-model="personalStatement.other"
                        class="w-full-i bg-green-200 padding-2 mb-8"
                        label=""
                        @update:modelValue="value = $event"
                    />
                    <div v-if="otherWordCount > maximumWordCount" class="flex justify-end mt-2 -mb-4">
                        <p class="text-right bg-red-300 px-2">Your 'Other relevant achievements' is too long. The maximum word count is {{ maximumWordCount }} words</p>
                    </div>
                </div>
                <div v-else>
                    <div class="flex flex-col-2">
                        <div class="w-1/2  text-left">
                            <p class="pl-1">Personal Statement:</p>
                        </div>
                        <div class="w-1/2 text-right pr-2">
                            <p>Word count: <span :class="(statementWordCount > maximumWordCount)? 'bg-red-300' : ''">{{ statementWordCount }}</span> / {{ maximumWordCount }}</p>
                        </div>
                    </div>
                    <textarea-input
                        v-model="personalStatement.statement"
                        class="w-full-i bg-green-200 padding-2 mb-8"
                        label=""
                        @update:modelValue="value = $event"
                    />
                    <div v-if="statementWordCount > maximumWordCount" class="flex justify-end mt-2 -mb-4">
                        <p class="text-right bg-red-300 px-2">Your 'Personal Statement' is too long. The maximum word count is {{ maximumWordCount }} words</p>
                    </div>
                </div>

                <div v-if="successMessage" class="success-message mt-4">{{ successMessage }}</div>
                <div v-if="serverError" class="server-error">{{ serverError }}</div>

                <div class="flex justify-end">
                    <application-component-save-button :disabled="formDirty && canSubmit" class="relative w-full lg:w-1/3 flex btn-green ml-auto mt-8 md:inline-block text-center"
                                                       type="submit"
                    >
                        Save Personal Statement
                        <div v-if="loading" class="lds-ring-container absolute top-2">
                            <div class="lds-ring">
                                <div /><div /><div /><div />
                            </div>
                        </div>
                    </application-component-save-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import ApplicationComponentSaveButton from '@/shared/ApplicationComponentSaveButton'
import TextareaInput from '@/shared/TextareaInput'

export default {
    components: {
        ApplicationComponentSaveButton,
        TextareaInput,
    },
    props: {
        applicationData: Object,
        applicationRef: String,
    },
    data() {
        return {
            maximumWordCount: 200,
            formDirty: false,
            loading: false,
            successMessage: '',
            serverError: '',
            personalStatement: {
                multiPersonalStatement: this.applicationData.supportMultiPersonalStatement ?? 0,
                passion: this.applicationData.personal_statement?.passion ?? '',
                motivation: this.applicationData.personal_statement?.motivation ?? '',
                aspiration: this.applicationData.personal_statement?.aspiration ?? '',
                other: this.applicationData.personal_statement?.other ?? '',
                statement: this.applicationData.personal_statement?.statement ?? '',
            },
        }
    },
    computed: {
        passionWordCount() {
            return this.computeWordCount('passion')
        },
        motivationWordCount() {
            return this.computeWordCount('motivation')
        },
        aspirationWordCount() {
            return this.computeWordCount('aspiration')
        },
        otherWordCount() {
            return this.computeWordCount('other')
        },
        statementWordCount() {
            return this.computeWordCount('statement')
        },
        canSubmit(){
            return this.passionWordCount <= this.maximumWordCount && this.motivationWordCount <= this.maximumWordCount && this.aspirationWordCount <= this.maximumWordCount
                && this.otherWordCount <= this.maximumWordCount && this.statementWordCount <= this.maximumWordCount
        },
    },
    mounted() {
        this.maximumWordCount = process.env.VUE_APP_MAXIMUM_WORD_PS_COUNT || 200
    },
    methods: {
        computeWordCount(attr) {
            if (this.personalStatement === null || this.personalStatement === undefined || this.personalStatement[attr] === null || this.personalStatement[attr] === undefined)
            {
                return 0
            }
            return this.personalStatement[attr].match(/\s+/g)?.length || 0
        },
        update() {
            this.loading = true
            this.$store.dispatch('updatePersonalStatement', {
                personalStatement: this.personalStatement,
                applicationRef: this.applicationRef,
            })
                .then((response) => {
                    this.loading = false
                    this.successMessage = response.data
                    this.serverError = ''
                })
                .catch(error => {
                    this.loading = false
                    this.successMessage = ''
                    this.serverError = error.response.data.message
                })
            this.formDirty = false
        },
        dirtyForm() {
            this.formDirty = true
            this.successMessage = ''
        },
    },
}
</script>